import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Facebook, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import confetti from "canvas-confetti";

const ROLE_LABEL = { vendor: "Vendor", event_creator: "Event Creator", marketplace_user: "Marketplace Seller" };
const SHARE_URL = "https://compasskids.co.za";
const SHARE_MESSAGE = {
  parent: "I just joined the Compass Kids family community! Find local vendors, events and meetups at compasskids.co.za @CompassKidsSA",
  vendor: "We're now on Compass Kids! Find us at compasskids.co.za @CompassKidsSA",
  event_creator: "I'm now creating family events on Compass Kids! Check it out at compasskids.co.za @CompassKidsSA",
  marketplace_user: "My marketplace is now live on Compass Kids! Shop at compasskids.co.za @CompassKidsSA",
};

// Facebook ignores pre-filled post text, so copy the message for the user to paste.
const shareToFacebook = (role) => {
  const msg = SHARE_MESSAGE[role];
  navigator.clipboard?.writeText(msg)
    .then(() => toast.success("Message copied, just paste it into your post"))
    .catch(() => toast.message(`Paste this into your post: ${msg}`));
  window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(SHARE_URL)}`, "fb-share", "width=626,height=436,noopener");
};

export default function LiveCelebration() {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const isParent = user?.role === "parent";
  const eligible = !!user && !user.admin_role && !user.live_celebrated_at
    && (isParent ? !!user.profile_completed : ROLE_LABEL[user.role] && user.vendor_status === "approved");
  const isLive = !!user && (user.bundle_member || user.subscription_status === "active");

  useEffect(() => { setOpen(!!eligible); }, [eligible, user?.user_id]);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => confetti({
      particleCount: 120, spread: 80, startVelocity: 45, origin: { y: 0.55 }, zIndex: 9999,
      colors: ["#A97F50", "#8A6440", "#C2982F", "#EFE2CF", "#F6ECD4"],
      disableForReducedMotion: true,
    }), 250);
    return () => clearTimeout(t);
  }, [open]);

  if (!eligible) return null;

  const dismiss = async () => {
    setOpen(false);
    try {
      const { data } = await api.post("/auth/live-celebrated");
      setUser((u) => (u ? { ...u, live_celebrated_at: data.live_celebrated_at } : u));
    } catch { /* shows again next visit */ }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent className="rounded-3xl text-center sm:max-w-md" data-testid="live-celebration-dialog">
        <div className="mx-auto w-16 h-16 rounded-full bg-terracotta-light flex items-center justify-center animate-in zoom-in-50 duration-500">
          <PartyPopper className="w-8 h-8 text-terracotta-dark" />
        </div>
        <DialogHeader className="sm:text-center">
          <DialogTitle className="text-2xl font-black text-forest text-center" data-testid="live-celebration-title">
            {isParent ? "Welcome to the family!" : isLive ? `Your ${ROLE_LABEL[user.role]} profile is now live!` : "You're approved!"}
          </DialogTitle>
          <DialogDescription className="text-forest/70 text-center">
            {isParent
              ? "Your Compass Kids profile is all set. Discover local vendors, events and meetups near you."
              : isLive
              ? "An admin has approved it — families across Compass Kids can now find you."
              : `Welcome to the village! Your ${ROLE_LABEL[user.role]} profile has been approved. Choose your plan whenever you're ready to go live.`}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          <Button onClick={() => shareToFacebook(user.role)} className="rounded-full bg-[#1877F2] hover:bg-[#166FE5] text-white px-6 transition-colors" data-testid="live-celebration-facebook-btn">
            <Facebook className="w-4 h-4 mr-2" /> Share to Facebook
          </Button>
          <Button onClick={dismiss} className="rounded-full bg-sage hover:bg-sage-dark text-white px-8" data-testid="live-celebration-ok-btn">
            Let's go
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
